import React, { useEffect, useState } from "react";
import { Table, Button, Modal, message, Card, Input, Alert, Image } from "antd";
import axios from "axios";
import AwardsForm from "./awardsForm";
import {
  BarsOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import CommonPagination from "../../components/common/pagination";

const Awards = () => {
  const BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const [awards, setAwards] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [openDetail, setOpenDetail] = useState(false);
  const [detailData, setDetailData] = useState(null);
  const [sortField, setSortField] = useState("id");
  const [sortOrder, setSortOrder] = useState("DESC");
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [total, setTotal] = useState(0);
  const [successMsg, setSuccessMsg] = useState(null);

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/api/awards-categories`);
      setCategories(res.data.data || []);
    } catch (error) {
      console.error("fetchCategories error:", error);
      message.error("Failed to fetch award categories");
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const fieldLabels = {
    category_id: "Category",
    image: "Image",
    title_id: "Title ID",
    title_en: "Title EN",
    created_at: "Created At",
    updated_at: "Updated At",
  };

  const detailOrder = [
    "category_id",
    "image",
    "title_id",
    "title_en",
    "created_at",
    "updated_at",
  ];

  const mapOrder = (order) => {
    if (order === "ascend") return "ASC";
    if (order === "descend") return "DESC";
    return "DESC";
  };

  const fetchAwards = async (pageNumber = 1) => {
    setLoading(true);
    try {
      const res = await axios.get(`${BASE_URL}/api/awards`, {
        params: {
          search: searchText,
          page: pageNumber,
          limit,
          sort: sortField,
          order: sortOrder,
        },
      });

      setAwards(res.data.data.rows || []);
      setTotal(res.data.data.count || 0);
      setPage(pageNumber);
    } catch (error) {
      console.error("fetchAwards errors:", error);
      message.error("Failed to fetch awards");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAwards(page);
  }, [page, searchText, sortField, sortOrder]);

  useEffect(() => {
    setPage(1);
  }, [searchText]);

  const getCategoryName = (categoryId) => {
    const found = categories.find((item) => item.id === categoryId);
    return found ? found.name : "-";
  };

  const handleSubmit = async (formData) => {
    try {
      if (editData) {
        await axios.put(`${BASE_URL}/api/awards/${editData.id}`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setSuccessMsg("Awards updated");
      } else {
        await axios.post(`${BASE_URL}/api/awards`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setSuccessMsg("Awards added");
      }

      setTimeout(() => setSuccessMsg(null), 3000);
      setOpen(false);
      setEditData(null);
      fetchAwards(1);
    } catch (err) {
      console.error(err);
      message.error("Failed to submit awards");
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${BASE_URL}/api/awards/${id}`);
      setSuccessMsg("Award deleted");
      setTimeout(() => setSuccessMsg(null), 3000);
      fetchAwards(page);
    } catch (err) {
      console.error(err);
      message.error("Failed to delete award");
    }
  };

  const columns = [
    {
      title: "No",
      render: (_, __, index) => (page - 1) * limit + index + 1,
    },
    {
      title: "Category",
      dataIndex: "category_id",
      render: (value) => getCategoryName(value),
      sorter: true,
    },
    {
      title: "Image",
      dataIndex: "image",
      render: (image) =>
        image ? (
          <img
            src={`${BASE_URL}${image}`}
            alt="awards"
            width={60}
            style={{ borderRadius: 8, objectFit: "cover" }}
          />
        ) : (
          "-"
        ),
    },
    {
      title: "Title ID",
      dataIndex: "title_id",
      sorter: true,
    },
    {
      title: "Title EN",
      dataIndex: "title_en",
      sorter: true,
    },
    {
      title: "Actions",
      render: (_, record) => (
        <div
          style={{
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
          }}
        >
          <Button
            style={{
              borderColor: "#ec4899",
              color: "#ec4899",
              marginRight: 8,
            }}
            icon={<BarsOutlined />}
            type="link"
            onClick={() => {
              setDetailData({ ...record });
              setOpenDetail(true);
            }}
          />

          <Button
            style={{
              backgroundColor: "#ec4899",
              borderColor: "#ec4899",
              color: "#fff",
              marginRight: 8,
            }}
            icon={<EditOutlined />}
            type="link"
            onClick={() => {
              setEditData({
                ...record,
                image: record.image
                  ? [
                      {
                        uid: "-1",
                        name: "image.jpg",
                        status: "done",
                        url: `${BASE_URL}${record.image}`,
                      },
                    ]
                  : [],
              });
              setOpen(true);
            }}
          />

          <Button
            style={{
              backgroundColor: "#374151",
              borderColor: "#374151",
              color: "#fff",
            }}
            icon={<DeleteOutlined />}
            type="link"
            onClick={() => {
              Modal.confirm({
                title: "Are you sure you want to delete this award?",
                centered: true,
                okText: "Yes",
                okType: "danger",
                cancelText: "No",
                onOk: () => handleDelete(record.id),
              });
            }}
          />
        </div>
      ),
    },
  ];

  return (
    <Card title="Awards">
      {successMsg && (
        <Alert
          message={successMsg}
          type="success"
          showIcon
          style={{
            marginBottom: 16,
            borderRadius: 8,
            backgroundColor: "#f6ffed",
            borderColor: "#f6ffed",
            color: "#389e0d",
          }}
        />
      )}

      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <Input.Search
          placeholder="Search Awards..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          allowClear
          style={{ width: 300 }}
        />

        <Button
          style={{
            backgroundColor: "#ec4899",
            borderColor: "#ec4899",
            color: "#fff",
          }}
          onClick={() => {
            setEditData(null);
            setOpen(true);
          }}
        >
          + Add Awards
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={awards}
        rowKey="id"
        loading={loading}
        pagination={false}
        style={{ marginTop: 20 }}
        onChange={(pagination, filters, sorter) => {
          if (sorter && sorter.field) {
            setSortField(sorter.field);
            setSortOrder(mapOrder(sorter.order));
          } else {
            setSortField("id");
            setSortOrder("DESC");
          }
        }}
      />

      <CommonPagination
        current={page}
        pageSize={limit}
        total={total}
        onChange={(newPage) => setPage(newPage)}
      />

      <Modal
        open={open}
        onCancel={() => {
          setOpen(false);
          setEditData(null);
        }}
        footer={null}
        destroyOnClose
      >
        <AwardsForm
          initialValues={editData}
          onSubmit={handleSubmit}
          editMode={!!editData}
          categories={categories}
        />
      </Modal>

      <Modal
        title="Awards Detail"
        open={openDetail}
        onCancel={() => setOpenDetail(false)}
        footer={[
          <Button
            key="close"
            style={{
              backgroundColor: "#ec4899",
              borderColor: "#ec4899",
              color: "#fff",
              marginRight: 8,
            }}
            onClick={() => setOpenDetail(false)}
          >
            Close
          </Button>,
        ]}
      >
        {detailData && (
          <Table
            dataSource={detailOrder.map((key) => {
              let value = detailData[key];

              if (key === "category_id") {
                value = getCategoryName(value);
              }

              if (key === "image") {
                if (value) {
                  const src = `${BASE_URL}${value}`;
                  value = (
                    <Image
                      src={src}
                      width={100}
                      style={{ borderRadius: 4 }}
                      preview={{
                        mask: (
                          <EyeOutlined
                            style={{ color: "white", fontSize: 18 }}
                          />
                        ),
                      }}
                    />
                  );
                } else {
                  value = "-";
                }
              }

              if ((key === "created_at" || key === "updated_at") && value) {
                value = dayjs(value).format("YYYY-MM-DD HH:mm:ss");
              }

              return {
                key,
                field: fieldLabels[key] || key,
                value,
              };
            })}
            columns={[
              {
                title: "Field",
                dataIndex: "field",
                key: "field",
                width: "30%",
                render: (text) => <strong>{text}</strong>,
              },
              {
                title: "Value",
                dataIndex: "value",
                key: "value",
              },
            ]}
            pagination={false}
            bordered
          />
        )}
      </Modal>
    </Card>
  );
};

export default Awards;
